import { FileResponseInterface } from 'services/interface';
import { isAetherResult } from 'components/details/overview/overviewResultMetrics';
import { isSandboxInProgress } from 'utils/sandbox/SandboxFunctions';
import { ScanResultCode } from 'types/Workflows';

export const cveRegex = /^(CVE-)?[1-2]\d{3}-\d{4,8}$/i;

export const getFileHash = async (file: File) => {
    const fileArrayBuffer = await file.arrayBuffer();
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', fileArrayBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const fileHash = hashArray
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();

    return fileHash;
};

type GetFileProgress = (processInfoProgress?: number, scanResultsProgress?: number) => number;

export const getFileProgress: GetFileProgress = (processInfoProgress, scanResultsProgress) => {
    if (typeof processInfoProgress === 'undefined' && typeof scanResultsProgress === 'undefined') {
        return 0;
    }

    if (typeof processInfoProgress === 'number' && typeof scanResultsProgress === 'number') {
        return Math.min(processInfoProgress, scanResultsProgress);
    }

    if (typeof processInfoProgress === 'number' && typeof scanResultsProgress === 'undefined') {
        return processInfoProgress;
    }

    if (typeof processInfoProgress === 'undefined' && typeof scanResultsProgress === 'number') {
        return scanResultsProgress;
    }

    return 100;
};


export const isFileScanInProgress = (fileData?: FileResponseInterface | null): boolean => {
    if (!fileData) {
        return true;
    }

    const processProgress = fileData.process_info?.progress_percentage;
    const scanProgress = fileData.scan_results?.progress_percentage;

    if (typeof processProgress === 'number' || typeof scanProgress === 'number') {
        return !getFileComplete(fileData);
    }

    // Only a live 255 means the scan is still running. A record carrying neither progress field nor an
    // aggregate (aborted, errored, a bare `{ file_info: {}, scan_results: {} }`) cannot be shown to be
    // running, and reading it as in-progress pins every caller to an "Analyzing…" state that never ends.
    const aggregate = fileData.scan_results?.scan_all_result_i;

    return aggregate === 255;
};

export const getFileComplete = (fileData?: FileResponseInterface | null): boolean => {
    const fileProgress = getFileProgress(
        fileData?.process_info?.progress_percentage,
        fileData?.scan_results?.progress_percentage,
    );

    const scanInProgress = fileData?.scan_results?.scan_all_result_i === 255;
    const isScanComplete = fileProgress === 100 && !scanInProgress;

    return isScanComplete;
};

/**
 * How long after the multiscan started an absent Threat Intelligence block still counts as "landing".
 *
 * The block follows the sandbox verdict by seconds, so the only thing this has to outlast is the sandbox
 * run itself — and only the tail of it, since a sandbox still in progress holds the state open on its own
 * branch below. Past this, a block that has not arrived is never going to: a TI engine that died without
 * writing one would otherwise pin the page to "Analyzing…" forever and keep `/v4/file` polling with it.
 */
const THREAT_INTELLIGENCE_WINDOW_MS = 30 * 60 * 1000;

/**
 * True while the Aether Threat Intelligence block (`threatintelligence_info`) can still arrive.
 *
 * TI is the stage after the Adaptive Sandbox and the last one in the pipeline, and the Platform delivers
 * both out of band: the sandbox verdict folds in and releases the file to 100% first, the TI block lands
 * on its own field seconds later, and nothing in `/v4/file` marks it as running meanwhile — no per-engine
 * progress is exposed and the stub is a bare `{ retried }`. So a settled Aether file that has a sandbox
 * verdict but no TI block yet is in that window: analysis in progress, not "no similar files" (MDCI-972).
 * No sandbox verdict on a settled file means the pipeline stopped earlier (file type / reputation) and
 * TI never runs; a sandbox that ran but produced nothing to hunt on arrives as `skip_message`.
 *
 * Bounded by `THREAT_INTELLIGENCE_WINDOW_MS` from the scan start: an archived record reopened by hash is
 * long past it and renders its final state, rather than an "Analyzing…" nothing on the page can resolve.
 * A record with no parseable `start_time` cannot be shown to be inside the window, so it is not pending.
 */
export const isThreatIntelligencePending = (fileData?: FileResponseInterface | null): boolean => {
    const threatIntelligence = fileData?.threatintelligence_info;

    // Landed, or explicitly skipped: whatever is on screen now is final.
    if (threatIntelligence?.final_verdict || threatIntelligence?.result || threatIntelligence?.skip_message) {
        return false;
    }

    if (isFileScanInProgress(fileData) || isSandboxInProgress(fileData?.sandbox_results)) {
        return true;
    }

    if (!isAetherResult(fileData ?? undefined) || fileData?.sandbox_results?.scan_result_i == null) {
        return false;
    }

    const startedAt = Date.parse(fileData?.scan_results?.start_time ?? '');

    return !Number.isNaN(startedAt) && Date.now() - startedAt < THREAT_INTELLIGENCE_WINDOW_MS;
};

/**
 * `process_info.profile` is the rule the backend resolved a scan to when no custom workflow was used:
 * the requested technology tokens joined with `_` (`multiscan_dlp_sanitize_unarchive`, `cdr`,
 * `aether_unarchive`, `sandbox_only`). Mapped to the names the Analyze scan options already use.
 * Unmapped tokens pass through rather than vanishing — a new backend token should read oddly, not silently.
 */
const SCAN_PROFILE_LABELS: Record<string, string> = {
    multiscan: 'Multiscanning',
    cdr: 'Deep CDR',
    'cdr-analysis': 'Deep CDR',
    sanitize: 'Deep CDR',
    dlp: 'Proactive DLP',
    aether: 'Adaptive Sandbox',
    sandbox: 'Adaptive Sandbox',
    // Only half of `sandbox_only`; the sandbox half already said it.
    only: '',
    unarchive: 'Unarchiving',
    coo: 'Country of Origin',
};

/** Translation keys for the technologies a `process_info.profile` names, in profile order, deduped. */
export const getScanProfileLabels = (profile?: string): string[] => {
    if (!profile) {
        return [];
    }
    const labels = profile.split('_').map((token) => SCAN_PROFILE_LABELS[token] ?? token);
    return labels.filter((label, index) => label !== '' && labels.indexOf(label) === index);
};

/**
 * Which kind of "the platform could not open this file" verdict came back, if any — the signal the
 * results page turns into a password prompt (MDCI-985).
 *
 * Only the aggregate `scan_all_result_i` is read, never the per-engine `scan_details`: one engine
 * reporting 15 under a clean rollup is that engine giving up on a file the rest of the pipeline
 * handled fine, and prompting there would ask for a password the file does not have. Gated on the
 * scan being finished so a transient code mid-poll cannot fire the prompt early.
 *
 * `process_info.verdicts` covers the file the rollup hides: an engine flagging the encrypted
 * container itself wins the aggregate (1, Infected) while the archive stays unopened — the scan then
 * reports `verdicts: ['Encrypted Archive', 'Infected']`, and drops the first once a password opens it.
 */
export const getPasswordProtectedKind = (
    fileData?: FileResponseInterface | null,
): 'archive' | 'document' | undefined => {
    if (!getFileComplete(fileData)) {
        return undefined;
    }

    const code = fileData?.scan_results?.scan_all_result_i;
    const verdicts = fileData?.process_info?.verdicts ?? [];

    if (code === ScanResultCode.EncryptedArchive || verdicts.includes('Encrypted Archive')) {
        return 'archive';
    }
    if (code === ScanResultCode.PasswordProtectedDocument || verdicts.includes('Password Protected Document')) {
        return 'document';
    }
    return undefined;
};
